// app/apps/campsite-explorer/components/FeatureGrid.tsx
import React from 'react';
import {
  FaCheckCircle, FaWifi, FaFire, FaShower, FaToilet,
  FaCaravan, FaCampground, FaWater, FaPlug, FaFish, FaHiking,
  FaSwimmer, FaTree, FaDog, FaWheelchair, FaParking, FaUtensils,
  FaShip, FaUmbrellaBeach, FaBed, FaHome,
  FaPhone, FaShoppingCart, FaGamepad, FaBicycle, FaTimes, FaMountain,
  FaUsers, FaDollarSign
} from 'react-icons/fa';
import { Feature } from '../types';
import { dedupeFeatures, featureKey } from '../lib/featureDedupe';

interface FeatureGridProps {
  features: Feature[];
}

// Extended feature icon mapping
const featureIcons: Record<string, React.ComponentType<any>> = {
  // Amenities
  'Toilets': FaToilet,
  'Showers': FaShower,
  'Shower': FaShower,
  'Hot Showers': FaShower,
  'Drinking Water': FaWater,
  'Power': FaPlug,
  'WiFi Internet': FaWifi,
  'WiFi': FaWifi,
  'Internet Cafe': FaWifi,

  // Accommodation
  'Caravans': FaCaravan,
  'Caravan Park': FaCaravan,
  'Motorhomes': FaCaravan,
  'Tent Site': FaCampground,
  'Camping Area': FaCampground,
  'Campground': FaCampground,
  'Backcountry': FaMountain,
  'Cabins': FaHome,
  'Resort': FaBed,
  
  // Activities
  'Fishing': FaFish,
  'Fishing Spot': FaFish,
  'Beach Fishing': FaFish,
  'Swimming': FaSwimmer,
  'Hiking': FaHiking,
  'Boating': FaShip,
  'Boat Ramp': FaShip,
  'Public Boat Ramp': FaShip,
  'Beach': FaUmbrellaBeach,
  'Bicycle': FaBicycle,
  
  // Facilities
  'BBQ': FaUtensils,
  'Camping Fee': FaDollarSign,
  'Campfire': FaFire,
  'No Pets': FaDog,
  'Pet Friendly': FaDog,
  'Pets Welcome': FaDog,
  'Accessible': FaWheelchair,
  'Shade': FaTree,
  'Parking': FaParking,
  'Public Phone': FaPhone,
  'Picnic Tables': FaUtensils,
  'Children\'s Play Facility': FaGamepad,
  'Playground': FaGamepad,
  
  // Default
  'Free Camping': FaCampground,
  'LPG Swap': FaFire,
  'Mobile Phone Coverage': FaPhone,
  'No Camping': FaTimes,
  'Dump Point': FaWater,
  'Fresh Water': FaWater,
  'Laundromat': FaShower,
  'Ice': FaShoppingCart,
  'No Fires': FaFire,

  // New canonical features
  'Unpowered Sites': FaCampground,
  'Dogs Welcome (On Lead)': FaDog,
  'Traditional Owner Managed': FaUsers,
  'Crocodile Risk — No Swimming': FaSwimmer,
  'Marine Stingers': FaWater
};

// Look icons up by featureKey so 'Dump point' / 'Caravan Parks' still find theirs.
const iconsByKey = new Map<string, React.ComponentType<any>>(
  Object.entries(featureIcons).map(([label, icon]) => [featureKey(label), icon]),
);

// Categorize features
const categorizeFeatures = (features: Feature[]) => {
  const categories: Record<string, string[]> = {
    amenities: ['Toilets', 'Showers', 'Shower', 'Hot Showers', 'Drinking Water', 'Power', 'WiFi Internet', 'WiFi', 'Laundromat', 'Public Phone'],
    activities: ['Fishing', 'Swimming', 'Hiking', 'Boating', 'Beach Fishing', 'Fishing Spot', 'Beach', 'Boat Ramp', 'Public Boat Ramp', 'Bicycle'],
    accommodation: ['Caravans', 'Motorhomes', 'Tent Site', 'Camping Area', 'Campground', 'Caravan Park', 'Cabins', 'Resort', 'Free Camping', 'Backcountry', 'Unpowered Sites'],
    facilities: ['BBQ', 'Campfire', 'Shade', 'Parking', 'Picnic Tables', 'Children\'s Play Facility', 'Playground', 'Dump Point', 'Ice'],
    fees: ['Camping Fee'],
    rules: ['No Pets', 'Pet Friendly', 'Pets Welcome', 'Accessible', 'No Camping', 'No Fires', 'Dogs Welcome (On Lead)', 'Traditional Owner Managed', 'Crocodile Risk — No Swimming', 'Marine Stingers'],
  };
  
  const categorized: Record<string, Feature[]> = {
    amenities: [],
    activities: [],
    accommodation: [],
    facilities: [],
    fees: [],
    rules: [],
    other: []
  };
  
  features.forEach(feature => {
    // Check if feature.desc exists
    if (!feature.desc) {
      categorized.other.push(feature);
      return;
    }
    
    const key = featureKey(feature.desc);
    let placed = false;
    for (const [category, items] of Object.entries(categories)) {
      if (items.some((label) => featureKey(label) === key)) {
        categorized[category].push(feature);
        placed = true;
        break;
      }
    }
    if (!placed) {
      categorized.other.push(feature);
    }
  });
  
  return categorized;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function formatSeason(start?: number | null, end?: number | null): string | null {
  if (!start || !end || start < 1 || start > 12 || end < 1 || end > 12) return null;
  return `${MONTHS[start - 1]}–${MONTHS[end - 1]}`;
}

const FeatureCard: React.FC<{ feature: Feature }> = ({ feature }) => {
  const Icon = iconsByKey.get(featureKey(feature.desc)) || FaCheckCircle;

  // Special styling for "No" features
  const isNegative = feature.desc?.toLowerCase().includes('no ') || false;

  return (
    <div className={`flex items-center space-x-3 p-3 rounded-xl border ${
      isNegative ? 'bg-red-50/60 border-red-100' : 'bg-white border-slate-200/80'
    }`}>
      <Icon
        className={`text-xl flex-shrink-0 ${isNegative ? 'text-red-500' : ''}`}
        style={isNegative ? undefined : { color: 'var(--brand-primary, #1a68d1)' }}
      />
      <span className={`text-sm font-medium ${isNegative ? 'text-red-800' : 'text-slate-700'}`}>
        {feature.desc || 'Unknown feature'}
        {formatSeason(feature.season_start, feature.season_end) && (
          <span className={`ml-1.5 text-xs font-semibold ${isNegative ? 'text-red-600' : 'text-slate-500'}`}>
            {formatSeason(feature.season_start, feature.season_end)}
          </span>
        )}
        {feature.note && (
          <span className="block text-xs font-normal text-slate-500 mt-0.5">{feature.note}</span>
        )}
      </span>
    </div>
  );
};

export default function FeatureGrid({ features }: FeatureGridProps) {
  // Defensive: callers should already pass a de-duplicated list.
  const categorizedFeatures = categorizeFeatures(dedupeFeatures(features));
  
  const categoryTitles: Record<string, string> = {
    amenities: 'Amenities',
    activities: 'Activities',
    accommodation: 'Accommodation Types',
    facilities: 'Facilities',
    fees: 'Fees',
    rules: 'Rules & Accessibility',
    other: 'Other Features'
  };

  return (
    <div className="ca-feature-grid bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 sm:p-7 lg:p-8">
      <h2 className="text-[22px] sm:text-2xl font-semibold text-slate-900 mb-6">What this place offers</h2>

      {Object.entries(categorizedFeatures).map(([category, categoryFeatures]) => {
        if (categoryFeatures.length === 0) return null;

        return (
          <div key={category} className="mb-6 last:mb-0">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">
              {categoryTitles[category]}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {categoryFeatures.map((feature) => (
                <FeatureCard key={feature.id} feature={feature} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}